import React, { useMemo, useState } from 'react'
import {
  Box,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import useIsMobile from '../../../helpers/useIsMobile'
import TaskCard from './TaskCard'
import { STATUSES, STATUS_META, resolveColor } from './taskHelpers'

const DraggableCard = ({ task, currentUserId, onOpen }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: task._id,
    data: { task },
  })
  return (
    <Box
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{ opacity: isDragging ? 0.35 : 1, touchAction: 'none' }}
    >
      <TaskCard
        task={task}
        currentUserId={currentUserId}
        onClick={() => onOpen(task._id)}
      />
    </Box>
  )
}

const BoardColumn = ({ status, tasks, currentUserId, onOpen, emptyText }) => {
  const theme = useTheme()
  const color = resolveColor(theme, STATUS_META[status].colorKey)
  const { setNodeRef, isOver } = useDroppable({ id: status })

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: isOver ? color : 'divider',
        bgcolor: alpha(color, isOver ? 0.12 : 0.06),
        overflow: 'hidden',
        transition: 'background-color 150ms ease, border-color 150ms ease',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 1.5,
          py: 1.25,
          borderBottom: '1px solid',
          borderBottomColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }}
          />
          <Typography variant="title" noWrap>
            {STATUS_META[status].label}
          </Typography>
        </Stack>
        <Chip
          label={tasks.length}
          size="small"
          sx={{
            minWidth: 34,
            fontWeight: 600,
            bgcolor: color,
            color: theme.palette.getContrastText(color),
          }}
        />
      </Stack>

      <Stack padding={1} gap={1} flex={1} minHeight={160}>
        {tasks.length === 0 && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: 'center',
              bgcolor: 'background.paper',
              borderStyle: 'dashed',
            }}
          >
            <Typography variant="body2">
              {isOver ? 'Drop to move here' : emptyText ?? 'Nothing here'}
            </Typography>
          </Paper>
        )}
        {tasks.map((task) => (
          <DraggableCard
            key={task._id}
            task={task}
            currentUserId={currentUserId}
            onOpen={onOpen}
          />
        ))}
      </Stack>
    </Paper>
  )
}

// Status columns with drag-to-move. Below `md` the columns become tabs and
// status changes go through the task drawer instead of dragging.
const TaskBoard = ({
  tasks,
  currentUserId,
  showingCompleted,
  onOpen,
  onStatusChange,
}) => {
  // With completed tasks hidden the column is still the drop target for
  // finishing a task — say so rather than looking broken
  const emptyTextFor = (status) =>
    status === 'Completed' && !showingCompleted
      ? 'Drag a card here to complete it'
      : undefined
  const isMobile = useIsMobile()
  const [mobileStatus, setMobileStatus] = useState('New')
  const [activeTask, setActiveTask] = useState(null)

  // A small distance threshold keeps plain clicks opening the drawer
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  const byStatus = useMemo(
    () =>
      Object.fromEntries(
        STATUSES.map((status) => [
          status,
          tasks.filter((task) => task.taskStatus === status),
        ]),
      ),
    [tasks],
  )

  if (isMobile) {
    return (
      <Stack gap={1.5}>
        <Paper variant="outlined">
          <Tabs
            value={mobileStatus}
            onChange={(e, value) => setMobileStatus(value)}
            variant="fullWidth"
          >
            {STATUSES.map((status) => (
              <Tab
                key={status}
                value={status}
                label={`${STATUS_META[status].label} ${byStatus[status].length}`}
                sx={{ minHeight: 44, fontSize: '0.75rem', px: 0.5, whiteSpace: 'nowrap' }}
              />
            ))}
          </Tabs>
        </Paper>
        <Stack gap={1}>
          {byStatus[mobileStatus].length === 0 && (
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: 'center', borderStyle: 'dashed' }}
            >
              <Typography variant="body2">
                {mobileStatus === 'Completed' && !showingCompleted
                  ? 'Switch on "Completed" above to see finished tasks'
                  : 'Nothing here'}
              </Typography>
            </Paper>
          )}
          {byStatus[mobileStatus].map((task) => (
            <TaskCard
              key={task._id}
              task={task}
              currentUserId={currentUserId}
              onClick={() => onOpen(task._id)}
            />
          ))}
        </Stack>
      </Stack>
    )
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => setActiveTask(active.data.current?.task)}
      onDragCancel={() => setActiveTask(null)}
      onDragEnd={({ active, over }) => {
        setActiveTask(null)
        const task = active.data.current?.task
        if (!task || !over || over.id === task.taskStatus) return
        onStatusChange(task._id, over.id)
      }}
    >
      <Stack direction="row" gap={1.5} alignItems="stretch">
        {STATUSES.map((status) => (
          <BoardColumn
            key={status}
            status={status}
            tasks={byStatus[status]}
            currentUserId={currentUserId}
            onOpen={onOpen}
            emptyText={emptyTextFor(status)}
          />
        ))}
      </Stack>
      <DragOverlay dropAnimation={null}>
        {activeTask && (
          <TaskCard task={activeTask} currentUserId={currentUserId} dragging />
        )}
      </DragOverlay>
    </DndContext>
  )
}

export default TaskBoard
